import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { clearStoredAuthSession } from "@/lib/authRecovery";

// Timeout wrapper to prevent infinite loading if database hangs
const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T | null> => {
  const timeout = new Promise<null>((resolve) => 
    setTimeout(() => resolve(null), ms)
  );
  return Promise.race([promise, timeout]);
};

// Profile caching with TTL to reduce API calls on token refresh
const PROFILE_CACHE_KEY = 'cached_profile';
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedProfile {
  profile: Profile;
  timestamp: number;
}

const getCachedProfile = (userId: string): Profile | null => {
  try {
    const cached = localStorage.getItem(`${PROFILE_CACHE_KEY}_${userId}`);
    if (!cached) return null;
    
    const { profile, timestamp }: CachedProfile = JSON.parse(cached);
    if (Date.now() - timestamp > PROFILE_CACHE_TTL) {
      localStorage.removeItem(`${PROFILE_CACHE_KEY}_${userId}`);
      return null;
    }
    
    return profile;
  } catch {
    return null;
  }
};

const setCachedProfile = (userId: string, profile: Profile): void => {
  try {
    const cacheEntry: CachedProfile = { profile, timestamp: Date.now() };
    localStorage.setItem(`${PROFILE_CACHE_KEY}_${userId}`, JSON.stringify(cacheEntry));
  } catch {
    // Ignore storage errors
  }
};

const clearCachedProfile = (userId: string): void => {
  try {
    localStorage.removeItem(`${PROFILE_CACHE_KEY}_${userId}`);
  } catch {
    // Ignore storage errors
  }
};

interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  is_pro: boolean;
  credits: number;
  free_ai_trial_used: boolean;
  pro_plan_type: string | null;
  pro_started_at: string | null;
  pro_expires_at: string | null;
  phone_number: string | null;
  weekly_digest_opt_out: boolean | null;
  /** Opted in to verified-recruiter search (off by default) */
  visible_to_recruiters?: boolean;
}

type AppRole = "admin" | "moderator" | "user";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAdmin: boolean;
  /** Full access to every builder feature. True for everyone: the AI builder is free. */
  isPro: boolean;
  /** Whether this user actually paid for a legacy Pro subscription (billing/settings). */
  hasPaidPlan: boolean;
  roles: AppRole[];
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = roles.includes("admin");
  // The AI builder is free for everyone (revenue comes from the expert services), so every
  // feature gate keyed on isPro is open. hasPaidPlan keeps the real subscription status.
  const hasPaidPlan = !!profile?.is_pro;
  const isPro = true;

  const fetchProfile = async (userId: string, useCache = true): Promise<Profile | null> => {
    // Check cache first for token refreshes
    if (useCache) {
      const cached = getCachedProfile(userId);
      if (cached) {
        console.log("[Auth] Using cached profile");
        return cached;
      }
    }
    
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }
      
      // Cache the profile
      if (data) {
        setCachedProfile(userId, data as Profile);
      }
      
      return data as Profile | null;
    } catch (error) {
      console.error("Error fetching profile:", error);
      return null;
    }
  };

  const fetchRoles = async (userId: string): Promise<AppRole[]> => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching roles:", error);
        return [];
      }
      return (data || []).map((r) => r.role as AppRole);
    } catch (error) {
      console.error("Error fetching roles:", error);
      return [];
    }
  };

  const refreshProfile = async () => {
    if (user) {
      clearCachedProfile(user.id);
      const [profileData, userRoles] = await Promise.all([
        withTimeout(fetchProfile(user.id, false), 5000),
        withTimeout(fetchRoles(user.id), 5000),
      ]);
      setProfile(profileData);
      setRoles(userRoles ?? []);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Listener for ONGOING auth changes (does NOT reset loading or roles)
    // This prevents the flicker during token refresh
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        // Skip initial session - we handle it separately below
        if (event === 'INITIAL_SESSION') return;

        // A fresh sign-in is activity. Without this, a last_activity_ts left from a visit over 24h
        // ago made the max-session check below sign people out the moment they signed in again.
        if (event === 'SIGNED_IN') {
          try { localStorage.setItem("last_activity_ts", String(Date.now())); } catch {}
        }
        
        // Update session/user immediately
        setSession(session);
        setUser(session?.user ?? null);

        // For ongoing changes (TOKEN_REFRESHED, SIGNED_IN after initial), 
        // update roles in background WITHOUT resetting them first
        // This preserves current roles during the async fetch
        if (session?.user) {
          // Block unverified emails from maintaining sessions
          if (!session.user.email_confirmed_at) {
            console.warn("[Auth] Unverified email detected, signing out");
            supabase.auth.signOut();
            if (isMounted) {
              setUser(null);
              setSession(null);
              setProfile(null);
              setRoles([]);
            }
            return;
          }
          // Fire and forget - don't await, don't touch isLoading
          // Roles stay as-is until new ones arrive
          fetchRoles(session.user.id).then((newRoles) => {
            if (isMounted) setRoles(newRoles);
          });
          fetchProfile(session.user.id).then((newProfile) => {
            if (isMounted) setProfile(newProfile);
          });
        } else {
          // User signed out - clear everything
          setProfile(null);
          setRoles([]);
        }
      }
    );

    // INITIAL load - waits for everything before setting isLoading = false
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (session?.user) {
          // Validate the token with the auth server. A stale/invalid JWT
          // (e.g. "missing sub claim" after key rotation) lives in localStorage
          // and getSession() does NOT validate it — so we verify with getUser().
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError || !userData?.user) {
            console.warn("[Auth] Invalid stored session, clearing:", userError?.message);
            // Local sign-out also drops the bad JWT from the client's memory,
            // otherwise every later request keeps sending it (403 bad_jwt).
            try { await supabase.auth.signOut({ scope: "local" }); } catch {}
            clearStoredAuthSession();
            if (isMounted) {
              setSession(null);
              setUser(null);
            }
            return;
          }

          setSession(session);
          setUser(userData.user);

          // Block unverified emails on initial load
          if (!userData.user.email_confirmed_at) {
            console.warn("[Auth] Unverified email on init, signing out");
            await supabase.auth.signOut();
            if (isMounted) {
              setSession(null);
              setUser(null);
            }
            return;
          }
          // CRITICAL: Wait for roles BEFORE setting loading to false
          const [profileData, userRoles] = await Promise.all([
            withTimeout(fetchProfile(userData.user.id), 5000),
            withTimeout(fetchRoles(userData.user.id), 5000),
          ]);
          if (isMounted) {
            setProfile(profileData);
            setRoles(userRoles ?? []);
          }
        } else {
          setSession(null);
          setUser(null);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Auto-logout: 30-min idle + 24-hour max session, with 1-min warning toast
  const lastActivityRef = useRef<number>(Date.now());
  const sessionStartRef = useRef<number>(Date.now());
  const throttleRef = useRef<boolean>(false);
  const warnedRef = useRef<boolean>(false);

  const updateActivity = useCallback(() => {
    if (throttleRef.current) return;
    throttleRef.current = true;
    lastActivityRef.current = Date.now();
    warnedRef.current = false;
    try { localStorage.setItem("last_activity_ts", String(Date.now())); } catch {}
    setTimeout(() => { throttleRef.current = false; }, 30000);
  }, []);

  useEffect(() => {
    if (!user) return;

    // On mount, if last activity stored is older than max session, force sign-out
    try {
      const stored = parseInt(localStorage.getItem("last_activity_ts") || "0", 10);
      if (stored && Date.now() - stored > 24 * 60 * 60 * 1000) {
        signOut();
        return;
      }
    } catch {}

    lastActivityRef.current = Date.now();
    sessionStartRef.current = Date.now();
    warnedRef.current = false;

    const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    const WARN_BEFORE = 60 * 1000; // 1 minute warning
    const MAX_SESSION = 24 * 60 * 60 * 1000;

    const events = ["mousemove", "keydown", "touchstart", "click", "scroll", "focus"] as const;
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));

    const intervalId = setInterval(() => {
      const now = Date.now();
      const idleMs = now - lastActivityRef.current;
      const sessionMs = now - sessionStartRef.current;

      if (idleMs > IDLE_TIMEOUT) {
        toast({ title: "Session expired", description: "You've been signed out due to inactivity." });
        signOut();
      } else if (sessionMs > MAX_SESSION) {
        toast({ title: "Session expired", description: "Your session has expired. Please sign in again." });
        signOut();
      } else if (!warnedRef.current && idleMs > IDLE_TIMEOUT - WARN_BEFORE) {
        warnedRef.current = true;
        toast({ title: "You'll be signed out soon", description: "Move your mouse or press a key to stay signed in." });
      }
    }, 30000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, updateActivity));
      clearInterval(intervalId);
    };
  }, [user, updateActivity]);

  const signOut = async () => {
    // Clear cached profile on sign out
    if (user) {
      clearCachedProfile(user.id);
    }
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
  };

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        session, 
        profile, 
        isLoading, 
        isAdmin,
        isPro,
        hasPaidPlan,
        roles,
        signOut, 
        refreshProfile 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
